import React, { useState, useMemo } from 'react';
import { ExpenseInputProps, FinancialTransactionItem } from '../../../types/dailyReport.ts';
import { saveFinancialTransactionData } from '../../../services/firebase/dailyReportFirebaseService.ts';
import { getMonthlyReceiptItems } from '../../../services/firebase/monthlyDataFirebaseService.ts';
import { TemporaryStaffSelector } from '../../../components/TemporaryStaffSelector.tsx';
import { formatCurrency } from '../../../utils/currencyUtils.ts';
import { DailyReportDebugLogger } from '../../../utils/debugUtils.ts';

// 카테고리 상수 정의
const EXPENSE_CATEGORIES = ['환불', '타팀 분배', '기타 지출'];
const INCOME_CATEGORIES = ['추가수납', '타팀 분배', '기타 수입'];

/**
 * 지출/수입 통합 입력 컴포넌트
 */
export const ExpenseInput: React.FC<ExpenseInputProps> = ({
  expenseData,
  onSave,
  currentDate,
  setExpenseData,
  receiptData,
  employees,
  financialTransactionData,
  setFinancialTransactionData
}) => {
  // 새로운 통합 거래 관리 상태
  const [transactionType, setTransactionType] = useState<'expense' | 'income'>('expense');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newTransaction, setNewTransaction] = useState<Partial<FinancialTransactionItem>>({
    chartNumber: '',
    name: '',
    assistName: '',
    cashierName: '',
    amount: 0,
    description: '',
    transactionType: 'expense'
  });

  // 차트번호 검색 관련 상태
  const [showAssistModal, setShowAssistModal] = useState(false);
  const [availableAssists, setAvailableAssists] = useState<string[]>([]);
  
  // 자동 저장 상태 관리
  const [showAutoSaveMessage, setShowAutoSaveMessage] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [assistTeam, setAssistTeam] = useState<string>('');

  // 현재 거래 유형에 따른 카테고리 목록
  const currentCategories = useMemo(() => {
    return transactionType === 'expense' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;
  }, [transactionType]);

  // 표시할 데이터 결정
  const displayItems = useMemo(() => {
    return financialTransactionData.items;
  }, [financialTransactionData]);

  // 차트번호로 어시스트 찾기 (Firebase dailyReports에서 검색)
  const findAssistByChartNumber = async (chartNum: string) => {
    if (!chartNum.trim()) {
      alert('차트번호를 입력해주세요.');
      return;
    }

    try {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[매칭] Firebase 매칭 프로세스 시작');
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[매칭] 현재 날짜:', currentDate);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[매칭] 입력된 차트번호:', chartNum);
      
      // Firebase dailyReports에서 해당 월의 모든 수납 데이터 가져오기
      const yearMonth = currentDate.substring(0, 7); // YYYY-MM 형식
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[매칭] ${yearMonth} 월의 수납 데이터를 가져오는 중...`);
      
      const monthlyItems = await getMonthlyReceiptItems(yearMonth);
      
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', 'Firebase에서 가져온 월별 수납 아이템:', monthlyItems);
      
      if (!monthlyItems || monthlyItems.length === 0) {
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', 'Firebase에 해당 월의 수납 데이터가 없음');
        // 직원 선택 모달 표시
        const employeeNames = employees?.map(emp => emp.name) || [];
        const allOptions = [...employeeNames, '기타'];
        setAvailableAssists(allOptions);
        setShowAssistModal(true);
        return;
      }

      // 월별 수납 아이템에서 해당 차트번호를 가진 모든 항목 찾기
      const inputChartNum = chartNum.trim();
      const inputChartNumWithoutZeros = parseInt(inputChartNum, 10).toString();
      
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[매칭] 입력된 차트번호: "${inputChartNum}"`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[매칭] 0 제거된 차트번호: "${inputChartNumWithoutZeros}"`);
      
      const matchingReceipts = monthlyItems.filter((item: any) => {
        if (!item.chartNumber) return false;
        
        const itemChartNum = item.chartNumber.toString().trim();
        const itemChartNumWithoutZeros = parseInt(itemChartNum, 10).toString();
        
        // 매칭 로직
        const exactMatch = itemChartNum === inputChartNum;
        const numericMatch = itemChartNumWithoutZeros === inputChartNumWithoutZeros;
        
        if (exactMatch || numericMatch) {
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[매칭] 매칭 성공: DB "${itemChartNum}" ↔ 입력 "${inputChartNum}" (정확매칭: ${exactMatch}, 숫자매칭: ${numericMatch})`);
          return true;
        }
        
        return false;
      });
      
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `차트번호 ${chartNum}로 ${yearMonth} 월에서 검색된 수납 항목 (${matchingReceipts.length}건):`, matchingReceipts);

      if (matchingReceipts.length === 0) {
        // 매칭되는 차트번호가 없으면 직원 선택 모달 표시
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '매칭되는 차트번호 없음');
        const employeeNames = employees?.map(emp => emp.name) || [];
        const allOptions = [...employeeNames, '기타'];
        setAvailableAssists(allOptions);
        setShowAssistModal(true);
        return;
      }

      // 어시스트 이름들 추출 (중복 제거)
      const assistNames = Array.from(new Set(
        matchingReceipts
          .map((item: any) => item.assistName)
          .filter((name: string) => name && name.trim())
      ));

      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '추출된 어시스트 이름들:', assistNames);

      // 환자 이름들도 추출하여 자동 설정
      const patientNames = Array.from(new Set(
        matchingReceipts
          .map((item: any) => item.name)
          .filter((name: string) => name && name.trim())
      ));
      
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '추출된 환자 이름들:', patientNames);
      
      // 가장 최근 이름으로 자동 설정 (날짜순 정렬)
      if (patientNames.length > 0) {
        const sortedReceipts = matchingReceipts.sort((a, b) => b.receiptDate.localeCompare(a.receiptDate));
        const mostRecentName = sortedReceipts[0]?.name;
        if (mostRecentName) {
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', '환자 이름 자동 설정:', mostRecentName);
          setNewTransaction(prev => ({ ...prev, name: mostRecentName }));
        }
      }

      if (assistNames.length === 0) {
        // 어시스트 정보가 없으면 직원 선택 모달 표시
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '어시스트 정보 없음');
        const employeeNames = employees?.map(emp => emp.name) || [];
        const allOptions = [...employeeNames, '기타'];
        setAvailableAssists(allOptions);
        setShowAssistModal(true);
        return;
      } else if (assistNames.length === 1) {
        // 어시스트가 하나만 있으면 자동 선택
        const selectedAssist = assistNames[0];
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '어시스트 자동 선택:', selectedAssist);
        setNewTransaction(prev => ({ ...prev, assistName: selectedAssist }));
      } else {
        // 여러 어시스트가 있으면 선택 모달 표시
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '여러 어시스트 선택 모달 표시');
        setAvailableAssists(assistNames);
        setShowAssistModal(true);
      }

    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '차트번호 검색 중 오류:', error);
      alert('차트번호 검색 중 오류가 발생했습니다.');
    }
  };

  // 거래 유형 변경 핸들러
  const handleTransactionTypeChange = (type: 'expense' | 'income') => {
    setTransactionType(type);
    setSelectedCategory('');
    setNewTransaction(prev => ({
      ...prev,
      transactionType: type,
      category: ''
    }));
  };

  // 금액 입력 처리 함수 (포매팅)
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // 숫자만 추출
    setNewTransaction(prev => ({ ...prev, amount: Number(value) }));
  };

  // 이중 적용 방지를 위해 데이터 변환 함수들을 제거
  // financialTransactions 데이터만 사용하여 월별 리포트에서 직접 처리
  
  // 새 거래 추가
  const handleAddTransaction = async () => {
    // 필수 필드 검증
    const requiredFields = [
      { field: 'chartNumber', name: '차트번호', value: newTransaction.chartNumber },
      { field: 'name', name: '성명', value: newTransaction.name },
      { field: 'assistName', name: '어시스트', value: newTransaction.assistName },
      { field: 'amount', name: '금액', value: newTransaction.amount }
    ];

    const missingFields = requiredFields.filter(item => !item.value);

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map(item => item.name).join(', ');
      alert(`다음 필수 항목을 입력해주세요: ${missingFieldNames}`);
      return;
    }
    
    const amountNumber = Number(newTransaction.amount);
    if (isNaN(amountNumber) || amountNumber <= 0) {
      alert('금액은 0보다 큰 숫자여야 합니다.');
      return;
    }

    // 팀 배정 디버깅 로그
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] assistTeam 상태값:', assistTeam);
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] newTransaction.assistTeam:', newTransaction.assistTeam);
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] assistName:', newTransaction.assistName);
    
    // employees 배열에서 어시스트의 팀 정보 찾기 (fallback)
    let employeeTeam = '';
    if (newTransaction.assistName && employees) {
      const employee = employees.find(emp => emp.name === newTransaction.assistName);
      if (employee) {
        employeeTeam = employee.team;
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정 fallback] employees에서 찾은 팀:', employeeTeam, 'for', newTransaction.assistName);
      } else {
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정 fallback] employees에서 찾지 못함:', newTransaction.assistName);
      }
    }
    
    // 팀 배정 우선순위: assistTeam 상태 > newTransaction.assistTeam > employees에서 조회 > 빈 문자열
    const finalAssistTeam = assistTeam || newTransaction.assistTeam || employeeTeam || '';
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] 최종 할당된 팀:', finalAssistTeam);
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] 우선순위 체크 - assistTeam:', assistTeam, 'newTransaction.assistTeam:', newTransaction.assistTeam, 'employeeTeam:', employeeTeam);

    const transaction: FinancialTransactionItem = {
      chartNumber: newTransaction.chartNumber!,
      name: newTransaction.name || '',
      assistName: newTransaction.assistName || '',
      assistTeam: finalAssistTeam,
      cashierName: newTransaction.cashierName || '',
      amount: Math.abs(amountNumber), // 절댓값으로 저장
      transactionType,
      description: newTransaction.description || '',
      category: selectedCategory
    };
    
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] 생성된 거래 객체:', transaction);

    // 통합 거래 관리
    const updatedItems = [...financialTransactionData.items, transaction];
    const totalExpense = updatedItems.filter(item => item.transactionType === 'expense').reduce((sum, item) => sum + item.amount, 0);
    const totalIncome = updatedItems.filter(item => item.transactionType === 'income').reduce((sum, item) => sum + item.amount, 0);
    
    const updatedData = {
      totalExpense,
      totalIncome,
      netAmount: totalIncome - totalExpense,
      items: updatedItems
    };
    
    setFinancialTransactionData(updatedData);
    
    // 자동 저장
    try {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[거래 추가] Firebase 자동 저장 시작:', updatedItems);
      await saveFinancialTransactionData(updatedData, currentDate);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[거래 추가] Firebase 자동 저장 성공');
      
      // 저장 상태 업데이트
      setLastSaveTime(new Date());
      setShowAutoSaveMessage(true);
      setTimeout(() => setShowAutoSaveMessage(false), 3000);
      
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[이중 적용 방지] 기존 구조 통합 제거 - financialTransactions만 사용');
      
      // 집계 업데이트
      try {
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[집계 업데이트] 메인 저장 함수 호출');
        onSave();
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '[집계 업데이트] 실패:', error);
      }
    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '[거래 추가] Firebase 자동 저장 실패:', error);
      alert('거래 항목 저장 중 오류가 발생했습니다.');
    }

    // 폼 초기화
    setNewTransaction({
      chartNumber: '',
      name: '',
      assistName: '',
      cashierName: '',
      amount: 0,
      description: '',
      transactionType
    });
    setSelectedCategory('');
    setAssistTeam('');
  };

  // 거래 삭제
  const handleDeleteTransaction = async (index: number) => {
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) {
      return;
    }
    
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '삭제 버튼 클릭됨. 인덱스:', index);
    const updatedItems = financialTransactionData.items.filter((_, i) => i !== index);
    const totalExpense = updatedItems.filter(item => item.transactionType === 'expense').reduce((sum, item) => sum + item.amount, 0);
    const totalIncome = updatedItems.filter(item => item.transactionType === 'income').reduce((sum, item) => sum + item.amount, 0);
    
    const updatedData = {
      totalExpense,
      totalIncome,
      netAmount: totalIncome - totalExpense,
      items: updatedItems
    };
    
    setFinancialTransactionData(updatedData);
    
    // 자동 저장
    try {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[거래 삭제] Firebase 자동 저장 시작');
      await saveFinancialTransactionData(updatedData, currentDate);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[거래 삭제] Firebase 자동 저장 성공');
      
      // 저장 상태 업데이트
      setLastSaveTime(new Date());
      setShowAutoSaveMessage(true);
      setTimeout(() => setShowAutoSaveMessage(false), 3000);
      
      // 집계 업데이트
      try {
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[집계 업데이트] 삭제 후 메인 저장 함수 호출');
        onSave();
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '[집계 업데이트] 삭제 후 실패:', error);
      }
    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '[거래 삭제] Firebase 자동 저장 실패:', error);
      alert('거래 항목 삭제 저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">
          거래 관리 ({currentDate})
        </h2>
      </div>
      
      {/* 기능 설명 */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>거래 관리:</strong> 지출과 수입을 함께 관리할 수 있습니다. 거래 유형을 선택하고 카테고리별로 분류할 수 있습니다.
        </p>
      </div>
      
      {/* 거래 유형 선택 */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">거래 유형</label>
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => handleTransactionTypeChange('expense')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              transactionType === 'expense'
                ? 'bg-red-100 text-red-700 border border-red-200'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            💸 지출
          </button>
          <button
            type="button"
            onClick={() => handleTransactionTypeChange('income')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              transactionType === 'income'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            💰 수입
          </button>
        </div>
      </div>

      {/* 거래 입력 폼 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-red-700 mb-1">차트번호 <span className="text-red-500">*</span></label>
          <div className="flex">
            <input
              type="text"
              value={newTransaction.chartNumber || ''}
              onChange={(e) => setNewTransaction(prev => ({ ...prev, chartNumber: e.target.value }))}
              onBlur={() => {
                if (newTransaction.chartNumber?.trim()) {
                  findAssistByChartNumber(newTransaction.chartNumber);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTransaction.chartNumber?.trim()) {
                  findAssistByChartNumber(newTransaction.chartNumber);
                }
              }}
              className="w-full px-3 py-2 border border-red-200 rounded-l-md text-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              placeholder="차트번호 입력 후 Tab/Enter"
              required
            />
            <button
              type="button"
              onClick={() => findAssistByChartNumber(newTransaction.chartNumber || '')}
              className="flex-shrink-0 px-3 py-2 bg-green-500 text-white rounded-r-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 text-xs"
              title="차트번호로 어시스트 검색"
            >
              🔍
            </button>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-red-700 mb-1">성명 <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={newTransaction.name || ''}
            onChange={(e) => setNewTransaction(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-3 py-2 border border-red-200 rounded-md text-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
            placeholder="성명"
          />
        </div>

        <div>
          <TemporaryStaffSelector
            selectedStaffName={newTransaction.assistName || ''}
            selectedTeam={assistTeam}
            onStaffChange={(staffName, assignedTeam) => {
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] TemporaryStaffSelector 콜백 호출됨');
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] - staffName:', staffName);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] - assignedTeam:', assignedTeam);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] - assignedTeam type:', typeof assignedTeam);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] - assignedTeam empty check:', !assignedTeam ? '빈 값' : '값 있음');
              
              // 직원 정보 확인
              const employee = employees?.find(emp => emp.name === staffName);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] - employee 정보:', employee);
              
              setNewTransaction(prev => ({ ...prev, assistName: staffName }));
              setAssistTeam(assignedTeam || '');
              
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀 배정] assistTeam 상태 업데이트 완료 - 설정된 값:', assignedTeam || '');
            }}
            label="어시스트 *"
            placeholder="어시스트 선택 (임시직은 팀 배정 가능)"
          />
        </div>

        <div>
          <TemporaryStaffSelector
            selectedStaffName={newTransaction.cashierName || ''}
            selectedTeam=""
            onStaffChange={(staffName) => {
              setNewTransaction(prev => ({ ...prev, cashierName: staffName }));
            }}
            label="수납자 (선택)"
            placeholder="수납자 선택"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-red-700 mb-1">금액 <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={newTransaction.amount ? Number(newTransaction.amount).toLocaleString() : ''}
            onChange={handleAmountChange}
            className="w-full px-3 py-2 border border-red-200 rounded-md text-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
            placeholder="금액을 입력하세요"
          />
        </div>

        {/* 카테고리 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">카테고리 (선택)</label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setNewTransaction(prev => ({ ...prev, category: e.target.value }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">선택</option>
            {currentCategories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">내역 (선택)</label>
          <input
            type="text"
            value={newTransaction.description || ''}
            onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="거래 내역을 입력하세요"
          />
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleAddTransaction}
          className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
            transactionType === 'expense'
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {transactionType === 'expense' ? '지출 추가' : '수입 추가'}
        </button>
        
        {/* 요약 정보 */}
        <div className="text-sm text-gray-600">
          <span className="text-red-600">지출: {financialTransactionData.totalExpense.toLocaleString()}원</span>
          <span className="mx-2">|</span>
          <span className="text-green-600">수입: {financialTransactionData.totalIncome.toLocaleString()}원</span>
          <span className="mx-2">|</span>
          <span className={`font-semibold ${financialTransactionData.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            순액: {financialTransactionData.netAmount.toLocaleString()}원
          </span>
        </div>
      </div>

      {/* 거래 목록 테이블 */}
      {displayItems && displayItems.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">차트번호</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">성명</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">어시스트</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수납자</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">구분</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">카테고리</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">금액</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">내역</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayItems.map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.chartNumber}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.assistName || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.cashierName || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.transactionType === 'expense' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {item.transactionType === 'expense' ? '💸 지출' : '💰 수입'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.category || '-'}</td>
                  <td className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${
                    item.transactionType === 'income' 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {item.transactionType === 'income' ? '+' : '-'}{formatCurrency(item.amount).replace('원', '')}원
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">{item.description}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    <button
                      onClick={() => handleDeleteTransaction(index)}
                      className="text-red-600 hover:text-red-900 text-xs"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-6">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-base font-medium text-gray-700 mb-1">
            거래 데이터가 없습니다
          </h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            {currentDate} 날짜의 거래 데이터가 없습니다. 위 입력 폼으로 거래를 추가해보세요.
          </p>
        </div>
      )}

      {/* 자동 저장 알림 메시지 */}
      {showAutoSaveMessage && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-green-800 text-sm">
            거래 항목이 자동으로 저장되었습니다.
          </span>
        </div>
      )}
      
      {/* 마지막 저장 시간 표시 */}
      {lastSaveTime && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-blue-800 text-sm">
              마지막 저장: {lastSaveTime.toLocaleString('ko-KR', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </span>
          </div>
        </div>
      )}

      {/* 어시스트 선택 모달 */}
      {showAssistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">어시스트 선택</h3>
              <button
                onClick={() => {
                  setShowAssistModal(false);
                  setAvailableAssists([]);
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                차트번호 <span className="font-medium">{newTransaction.chartNumber}</span>에 대한 어시스트를 선택하세요.
              </p>
            </div>
            
            <div className="mb-4 max-h-60 overflow-y-auto">
              {availableAssists.map((assist, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setNewTransaction(prev => ({ ...prev, assistName: assist }));
                    setAssistTeam(''); // 어시스트 선택 시 팀 초기화
                    setShowAssistModal(false);
                    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '어시스트 선택:', assist);
                  }}
                  className="w-full text-left px-3 py-2 mb-2 border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {assist}
                </button>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => {
                  setShowAssistModal(false);
                  setAvailableAssists([]);
                  DailyReportDebugLogger.log('MONTHLY_CALCULATION', '어시스트 선택 취소');
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseInput;